import { BarChart3, CircleHelp, MessageSquare, Eye, Home, Info, LogOut, Megaphone, Phone, Settings as SettingsIcon, Shield, UserCircle2, WifiOff, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { impersonation } from '../lib/api';
import { useSupportUnread } from './supportUnread';
import { ContactCard, useSupportContact } from './ContactUs';
import { Sheet } from '../ui/Sheet';
import { initials } from '../lib/format';
import { Logo } from '../ui/Logo';
import { InstallBanner } from './InstallBanner';
import { useParent } from './ParentContext';
import { FamilySwitcher } from './FamilySwitcher';
import { BillingNoticeBanner } from './BillingNoticeBanner';

function AccountMenu() {
  const { parent, signOut } = useParent();
  const supportUnread = useSupportUnread();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const contact = useSupportContact();
  const [contacting, setContacting] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };
  return (
    <div className="account-menu" ref={ref}>
      <button className="account-btn" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} aria-label="Account menu">
        <span className="account-avatar">{initials(parent.name)}</span>
        {supportUnread > 0 && <span className="nav-dot avatar" aria-label="New Help & Feedback reply" />}
        <UserCircle2 size={18} className="narrow-only" />
      </button>
      {open && (
        <div className="account-pop card" role="menu">
          <div className="account-who">
            <strong>{parent.name}</strong>
            <span className="muted small">{parent.email}</span>
          </div>
          <button role="menuitem" onClick={() => go('/about')}>
            <Info size={16} /> About
          </button>
          <button role="menuitem" onClick={() => go('/help')}>
            <CircleHelp size={16} /> Help
          </button>
          {/* Feedback is its own entry (#534): your requests to support, with the new-reply dot. */}
          <button role="menuitem" onClick={() => go('/feedback')}>
            <MessageSquare size={16} /> Feedback
            {supportUnread > 0 && <span className="nav-dot" aria-label={`${supportUnread} new ${supportUnread === 1 ? 'reply' : 'replies'}`} />}
          </button>
          {contact && (
            <button
              role="menuitem"
              onClick={() => {
                setOpen(false);
                setContacting(true);
              }}
            >
              <Phone size={16} /> Contact us
            </button>
          )}
          <button role="menuitem" className="danger-text" onClick={signOut}>
            <LogOut size={16} /> Sign out
          </button>
        </div>
      )}
      {contacting && contact && (
        <Sheet title="Contact us" onClose={() => setContacting(false)}>
          <ContactCard contact={contact} />
        </Sheet>
      )}
    </div>
  );
}

export function Shell() {
  const { parent, connected, toasts, dismiss } = useParent();
  const supportUnread = useSupportUnread();
  const viewingAs = impersonation.active();
  return (
    <div className="shell">
      {viewingAs && (
        <div className="impersonation-bar" role="status">
          <Eye size={16} />
          <span className="grow">
            Viewing as <strong>{viewingAs}</strong>. Anything you change here is real.
          </span>
          <button className="btn small" onClick={() => impersonation.end().then(() => location.assign('/admin'))}>
            Back to admin
          </button>
        </div>
      )}
      <InstallBanner />
      <header className="topbar">
        <div className="topbar-start">
          <NavLink to="/" className="topbar-home">
            <Logo />
          </NavLink>
          {/* Someone in two families works in one at a time (#652). */}
          <FamilySwitcher compact reloadTo="/" />
        </div>
        <nav className="topnav">
          <NavLink to="/" end>
            Home
          </NavLink>
          <NavLink to="/reports">Reports</NavLink>
          {/* Help & Feedback lives in Settings and the account menu, not up here (#527); a new reply shows a dot on Settings. */}
          <NavLink to="/settings">
            Settings
            {supportUnread > 0 && <span className="nav-dot" aria-label={`${supportUnread} new Help & Feedback ${supportUnread === 1 ? 'reply' : 'replies'}`} />}
          </NavLink>
          {/* Brand Ambassadors' own page (#743), a tab rather than tucked in the account menu. */}
          {parent.ambassador && <NavLink to="/ambassador">Ambassador</NavLink>}
          {parent.isAdmin && <NavLink to="/admin">Admin</NavLink>}
        </nav>
        <div className="topbar-end">
          {!connected && (
            <span className="offline-pill" title="Reconnecting to ClassGuardian">
              <WifiOff size={14} /> Reconnecting
            </span>
          )}
          <AccountMenu />
        </div>
      </header>

      <main className="content">
        <BillingNoticeBanner />
        <Outlet />
      </main>

      <nav className="bottomnav">
        <NavLink to="/" end>
          <Home size={22} />
          <span>Home</span>
        </NavLink>
        <NavLink to="/reports">
          <BarChart3 size={22} />
          <span>Reports</span>
        </NavLink>
        <NavLink to="/settings">
          <SettingsIcon size={22} />
          <span>Settings</span>
          {supportUnread > 0 && <span className="nav-dot bottom" aria-label="New Help & Feedback reply" />}
        </NavLink>
        {parent.ambassador && (
          <NavLink to="/ambassador">
            <Megaphone size={22} />
            <span>Ambassador</span>
          </NavLink>
        )}
        {parent.isAdmin && (
          <NavLink to="/admin">
            <Shield size={22} />
            <span>Admin</span>
          </NavLink>
        )}
      </nav>

      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.tone}`}>
            <span>{t.text}</span>
            {t.action && (
              <button
                className="btn small"
                onClick={() => {
                  t.action!.run();
                  dismiss(t.id);
                }}
              >
                {t.action.label}
              </button>
            )}
            <button className="icon-btn" onClick={() => dismiss(t.id)} aria-label="Dismiss">
              <X size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

